import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

import { BUCKET_ROTURAS, anularTrabajo, trabajos } from "../services/puerto";
import { actual as quienSoy } from "../services/quien";
import { desdeClave, ruta as rutaDeUnidad } from "../shared/unidad";
import FotoPrivada from "../components/funcionales/FotoPrivada";
import BotonConfirmar from "../components/funcionales/BotonConfirmar";
import ModoEdicion from "../components/funcionales/ModoEdicion";
import Loading from "./Loading";

/* Los trabajos que el puerto le hace a cada camión.
 *
 * ── TABLA PROPIA, HISTORIAL COMPARTIDO ─────────────────────────────────────
 *
 * `puerto_trabajos` no es `taller_tickets`: aquélla es una máquina de estados
 * que mide SLA y producción, y esto es una declaración de algo ya hecho.
 * Metidos ahí moverían números que alguien lee.
 *
 * Pero **el historial del camión es uno solo**, y por eso cada fila enlaza a la
 * ficha de su unidad: ahí se ven juntos los trabajos de puerto y los tickets
 * del taller, que es donde alguien pregunta «¿qué le hicieron?».
 *
 * ── NO SE CARGA DESDE ACÁ ──────────────────────────────────────────────────
 *
 * Entra por WhatsApp: `774 cambio de pastillas`. Un formulario acá sería un
 * segundo lugar para lo mismo, y el día que se cargue en el que no es ese
 * trabajo no aparece en el historial. Lo único que se escribe es **anular**.
 *
 * ── Y ANULAR NO ES BORRAR ──────────────────────────────────────────────────
 *
 * La fila queda con quién la anuló, mismo criterio que un movimiento anulado y
 * que un ticket cancelado: que alguien haya decidido que ese trabajo no va es
 * parte de la historia, y hacerlo desaparecer deja el historial del camión sin
 * poder explicar por qué hay un hueco.
 */

const cuando = (iso) => {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() - 3 * 3600 * 1000);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
};

/* Las fotos de la rotura. Se mandan por WhatsApp con el interno al pie.
 *
 * ── UN BOTÓN POR FOTO, Y NUMERADO ─────────────────────────────────────────
 *
 * Un solo «ver 4 fotos» tendría que abrir cuatro pestañas de golpe —que el
 * navegador bloquea— o llevar a una galería que esta pantalla no tiene. Con un
 * botón por foto se abre la que se quiere mirar, y de paso **cuántas hay se ve
 * sin apretar nada**: es el dato que dice si un siniestro está documentado o
 * si alguien mandó una sola foto de lejos.
 *
 * ── Y EL HUECO NO SE DIBUJA COMO UNA FALTA ────────────────────────────────
 *
 * La enorme mayoría de los trabajos no lleva foto y está perfecto: «774 cambio
 * de pastillas» no necesita ninguna. Un «sin foto» en rojo en cada fila
 * convertiría lo normal en un pendiente, y a la semana la columna se deja de
 * mirar. Vacío es vacío.
 */
const Fotos = ({ rutas }) => {
  const fs = (rutas || []).filter(Boolean);
  if (!fs.length) return null;
  if (fs.length === 1) {
    return <FotoPrivada ruta={fs[0]} bucket={BUCKET_ROTURAS} />;
  }
  return (
    <span className="pt-fotos">
      {fs.map((r, i) => (
        <FotoPrivada
          key={r}
          ruta={r}
          bucket={BUCKET_ROTURAS}
          etiqueta={`foto ${i + 1}`}
        />
      ))}
    </span>
  );
};

const PuertoTrabajos = () => {
  const { permisos = null } = useOutletContext() || {};
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(false);
  const [yendo, setYendo] = useState(null);

  /* Mientras los permisos no llegaron se asume que puede: esconder el lápiz
   * medio segundo y después mostrarlo se lee como que la pantalla titila. */
  const puedeEditar = !permisos || permisos.includes("cargar.puerto");
  const tocando = puedeEditar && editando;

  const traer = useCallback(async () => {
    try {
      setFilas(await trabajos());
    } catch (e) {
      setError(e.message || "No se pudieron leer los trabajos");
    }
  }, []);

  useEffect(() => {
    traer();
  }, [traer]);

  const anular = async (id) => {
    setYendo(id);
    try {
      /* Queda con QUIÉN lo anuló, del mismo lugar que sella el resto de la
       * escritura del hub. Sin autor, «¿dónde está el trabajo que cargué?» no
       * tiene a quién preguntarle. */
      await anularTrabajo(id, quienSoy()?.mail || null);
      // Se saca de la lista en memoria: volver a pedirla mueve todo de lugar
      // justo después de tocar una fila.
      setFilas((prev) => (prev || []).filter((f) => f.id !== id));
    } catch (e) {
      setError(e.message || "No se pudo anular");
    } finally {
      setYendo(null);
    }
  };

  if (error && !filas) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!filas) return <Loading />;

  return (
    <div className="docs">
      <div className="tk-filtros">
        <ModoEdicion
          activo={editando}
          onCambiar={setEditando}
          puede={puedeEditar}
          que="los trabajos"
        />
      </div>

      {/* `suave` y no `.panel-aviso` a secas: sin el modificador esto es un
          overlay absoluto que tapa el rail y la cabecera, y un error de
          carga dejaría la pantalla sin salida. */}
      {error && <p className="panel-aviso suave">{error}</p>}

      {filas.length === 0 && (
        <p className="panel-aviso suave">
          Todavía no hay ningún trabajo anotado.
        </p>
      )}

      {filas.length > 0 && (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                <th>Unidad</th>
                <th>Qué se le hizo</th>
                <th>Fotos</th>
                <th>Quién</th>
                <th>Cuándo</th>
                {tocando && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => {
                const u = desdeClave(f.interno);
                return (
                  <tr key={f.id}>
                    <td>
                      {/* ENLAZA A LA FICHA, que es donde el historial está
                          junto: los trabajos de puerto y los tickets del taller
                          en una sola línea de tiempo. Un registro que obliga a
                          ir a buscar el resto a otra pantalla no contesta la
                          pregunta que alguien tenía. */}
                      {u ? (
                        <Link to={rutaDeUnidad(u.tipo, u.interno)}>
                          <b>{f.interno}</b>
                        </Link>
                      ) : (
                        <b>{f.interno}</b>
                      )}
                    </td>
                    <td>{f.tarea}</td>
                    <td>
                      <Fotos rutas={f.fotos} />
                    </td>
                    <td>
                      {f.persona || <span className="sin-dato">sin nombre</span>}
                      {f.rol && <span className="sub">{f.rol}</span>}
                    </td>
                    <td className="tenue">{cuando(f.creado_en)}</td>
                    {tocando && (
                      <td>
                        {/* PREGUNTA ANTES. Desde acá un anulado no se puede
                            volver a traer, y el botón está a un dedo de la
                            fila de al lado. */}
                        <BotonConfirmar
                          className="link-button"
                          disabled={yendo === f.id}
                          pregunta="¿Anular?"
                          si="Sí, anular"
                          onConfirmar={() => anular(f.id)}
                        >
                          {yendo === f.id ? "Anulando…" : "Anular"}
                        </BotonConfirmar>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="docs-pie">
        Se cargan por WhatsApp. Las fotos, con el interno y el texto al pie:{" "}
        <code>010 golpe en el paragolpes</code>.
      </p>
    </div>
  );
};

export default PuertoTrabajos;
